import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSEO } from '../hooks/useSEO';
import { useJsonLd, faqPageSchema, breadcrumbSchema } from '../hooks/useJsonLd';

export const FAQPage = () => {
  const faqs = [
    {
      question: "What sizes are available?",
      answer: "Our Faja Colombiana is available in sizes S through 3XL. Please refer to our size chart: S (US 2-4), M (US 6-8), L (US 10-12), XL (US 14-16), 2XL (US 18-20), 3XL (US 22-24). For best fit, measure your bust, waist, and hips and compare to our size guide. If between sizes, size up for comfort or down for maximum compression."
    },
    {
      question: "How do I care for my bodysuit?",
      answer: "Hand wash in cold water with mild detergent and lay flat to dry. This will help maintain the shape and quality of the fabric."
    },
    {
      question: "Is the bodysuit comfortable for all-day wear?",
      answer: "Absolutely! The breathable, stretchy fabric is designed for maximum comfort. Many customers wear it from morning to night without any discomfort."
    },
    {
      question: "What's your return policy?",
      answer: "We offer a 30-Day Satisfaction Guarantee. If you're not completely satisfied, return it for a full refund within 30 days of purchase."
    },
    {
      question: "Where do you ship?",
      answer: "We offer free shipping within the United States (3-5 business days). We also ship to Canada with applicable shipping fees calculated at checkout."
    },
    {
      question: "How long does shipping take?",
      answer: "US orders: Free shipping, typically 3-5 business days. Canadian orders: 7-10 business days with shipping fees applied at checkout."
    },
    {
      question: "Will it show under my clothes?",
      answer: "No! The seamless design and smooth fabric make it completely invisible under clothing — that's the magic of a true Faja Colombiana."
    }
  ];

  useSEO({
    title: 'FAQ — Faja Sizing, Care, Shipping & Returns | CurveeForYou',
    description: 'Everything you need to know about your Faja Colombiana: sizing, care, returns, shipping, comfort, and whether it shows under clothes.',
    canonical: 'https://curveeforyou.com/faq'
  });

  useJsonLd([
    faqPageSchema(faqs),
    breadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'FAQ', url: '/faq' }
    ])
  ]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-[#7d1f3d]">
              CurveeForYou
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-[#7d1f3d] text-[#7d1f3d] hover:bg-[#7d1f3d] hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Shop
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-16 max-w-4xl">
        <h1 className="text-5xl font-bold text-[#7d1f3d] mb-6">Frequently Asked Questions</h1>
        <p className="text-xl text-gray-600 mb-12">Everything you need to know about your Full Body Faja Colombiana</p>

        <div className="space-y-6">
          {faqs.map((faq, idx) => (
            <div key={idx} className="bg-white border-2 border-gray-100 rounded-xl p-6 shadow-md hover:shadow-lg transition-all">
              <h2 className="text-left font-semibold text-[#7d1f3d] text-lg mb-3">
                {faq.question}
              </h2>
              <p className="text-gray-600 leading-relaxed">
                {faq.answer}
              </p>
            </div>
          ))}
        </div>

        {/* Still Have Questions */}
        <div className="mt-16 bg-gradient-to-br from-[#7d1f3d] to-[#9d2f4d] text-white rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold mb-4">Still Have Questions?</h3>
          <p className="text-lg mb-6 opacity-90">We&apos;re here to help!</p>
          <Link to="/contact">
            <Button className="bg-white text-[#7d1f3d] hover:bg-gray-100">
              Contact Customer Service
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#7d1f3d] text-white py-8 mt-16">
        <div className="container mx-auto px-6 text-center">
          <div className="flex flex-wrap justify-center gap-6 mb-4">
            <Link to="/faq" className="hover:text-[#d4af37] transition-colors">FAQ</Link>
            <Link to="/shipping" className="hover:text-[#d4af37] transition-colors">Shipping</Link>
            <Link to="/returns" className="hover:text-[#d4af37] transition-colors">Returns</Link>
            <Link to="/size-guide" className="hover:text-[#d4af37] transition-colors">Size Guide</Link>
            <Link to="/contact" className="hover:text-[#d4af37] transition-colors">Contact</Link>
          </div>
          <div className="text-sm opacity-90 mb-3 leading-relaxed">
            <p className="font-semibold mb-1">CurveeForYou</p>
            <p>1209 Mountain Road Place Northeast, Suite N</p>
            <p>Albuquerque, NM 87110, United States</p>
            <p className="mt-2">
              <a href="tel:+18002985173" className="hover:text-[#d4af37] transition-colors">Tel: 800 298 5173</a>
              {' · '}
              <a href="mailto:info@curveeforyou.com" className="hover:text-[#d4af37] transition-colors">info@curveeforyou.com</a>
            </p>
          </div>
          <p className="text-sm opacity-80">© 2025 CurveeForYou. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};
